import React from "react";
import { Link } from "react-router-dom";
import { LOGO_URL_TRANSPARENT, API } from "@/lib/config";
import { Phone, Mail, MapPin, Download, QrCode, Globe } from "lucide-react";

export default function Footer() {
  const cardUrl = typeof window !== "undefined" ? `${window.location.origin}/card` : "/card";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=6&data=${encodeURIComponent(cardUrl)}&bgcolor=1a1a1a&color=fdfbf7`;

  return (
    <footer data-testid="site-footer" className="mt-24 bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-14 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL_TRANSPARENT} alt="JFeva Solutions, LLC" className="h-14 w-14 object-contain" />
            <div>
              <div className="font-display text-2xl">JFeva Solutions, LLC</div>
              <div className="eyebrow opacity-70">Our quality will floor you.</div>
            </div>
          </div>
          <p className="mt-6 text-sm text-white/70 max-w-md">
            Florida&apos;s craftsmen for flooring &amp; tile installation. Hardwood, laminate, vinyl, and custom tile —
            installed to last.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow opacity-60 mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><Link data-testid="footer-nav-about" className="hover:text-white text-white/70" to="/about">About</Link></li>
            <li><Link data-testid="footer-nav-services" className="hover:text-white text-white/70" to="/services">Services</Link></li>
            <li><Link data-testid="footer-nav-gallery" className="hover:text-white text-white/70" to="/gallery">Gallery</Link></li>
            <li><Link data-testid="footer-nav-quote" className="hover:text-white text-white/70" to="/quote">Request a Quote</Link></li>
            <li><Link data-testid="footer-nav-book" className="hover:text-white text-white/70" to="/booking">Book Installation</Link></li>
            <li><Link data-testid="footer-nav-card" className="hover:text-white text-white/70" to="/card">Business Card</Link></li>
            <li><Link data-testid="footer-nav-process" className="hover:text-white text-white/70" to="/what-to-expect">What to Expect</Link></li>
            <li><Link data-testid="footer-nav-warranty" className="hover:text-white text-white/70" to="/warranty">Warranty</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow opacity-60 mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Phone className="h-4 w-4 opacity-70"/>
              <a href="tel:8502520162" data-testid="footer-phone" className="hover:text-white text-white/80">850-252-0162</a>
            </li>
            <li className="flex items-center gap-3"><Mail className="h-4 w-4 opacity-70"/>
              <a href="mailto:info@jfeva.com" data-testid="footer-email" className="hover:text-white text-white/80">info@jfeva.com</a>
            </li>
            <li className="flex items-center gap-3"><MapPin className="h-4 w-4 opacity-70"/>
              <span className="text-white/80">Florida &amp; surrounding areas</span>
            </li>
            <li className="flex items-center gap-3"><Globe className="h-4 w-4 opacity-70"/>
              <a href="https://jfeva.com" target="_blank" rel="noreferrer" data-testid="footer-website" className="hover:text-white text-white/80">jfeva.com</a>
            </li>
          </ul>
        </div>

        {/* Save to Contacts mini-CTA */}
        <div className="md:col-span-3" data-testid="footer-save-cta">
          <div className="eyebrow opacity-60 mb-4 flex items-center gap-2"><QrCode className="h-3.5 w-3.5"/> Save Jfeva</div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4">
            <img
              src={qrUrl}
              alt="Scan to save JFeva Solutions, LLC to your contacts"
              className="h-24 w-24 rounded-lg bg-[color:var(--brand-ink)] p-1 shrink-0"
              data-testid="footer-save-qr"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium leading-tight">Scan or tap to save.</div>
              <div className="text-xs text-white/60 mt-1 leading-snug">Adds our phone, email &amp; site to your contacts in one tap.</div>
              <a
                href={`${API}/vcard`}
                download="Jfeva-Solutions-LLC.vcf"
                data-testid="footer-save-vcf"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-cream)] text-[color:var(--brand-ink)] px-3 py-1.5 text-xs font-medium hover:-translate-y-0.5 transition-transform"
              >
                <Download className="h-3.5 w-3.5"/> Save contact
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} JFeva Solutions, LLC. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <Link data-testid="footer-privacy" to="/privacy" className="hover:text-white/80">Privacy</Link>
            <Link data-testid="footer-terms" to="/terms" className="hover:text-white/80">Terms</Link>
            <Link data-testid="footer-refund" to="/refund" className="hover:text-white/80">Refund</Link>
            <Link data-testid="footer-admin" to="/admin" className="hover:text-white/80">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
